import React, { useState, useEffect, useRef } from 'react'
import { 
  X, 
  ScrollText, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  ExternalLink,
  FileText,
  AlertCircle
} from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * Props for the TermsOfServiceModal component
 * Controls modal visibility and callbacks for user actions
 */
interface TermsOfServiceModalProps {
  isOpen: boolean
  onAccept: () => void
  onDecline: () => void
}

/**
 * TermsOfServiceModal Component
 * 
 * A modal dialog that prompts users to accept the Terms of Service
 * before connecting their wallet. This is a legal requirement that ensures
 * users acknowledge the risks and terms of using the platform.
 * 
 * Features:
 * - Accessible modal with proper focus management (WCAG 2.1 compliant)
 * - Checkbox for explicit consent
 * - Link to full Terms of Service page
 * - Summary of key terms
 * - Accept/Decline buttons
 * - Escape key and backdrop click to close
 * - Focus trap within modal when open
 * 
 * Note: Uses standard <a> tags instead of React Router's <Link> because this
 * modal is rendered outside of the BrowserRouter context (via TermsProvider
 * which is positioned above the Router in the component tree).
 */
export function TermsOfServiceModal({
  isOpen,
  onAccept,
  onDecline
}: TermsOfServiceModalProps): JSX.Element | null {
  const [isChecked, setIsChecked] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)
  const acceptButtonRef = useRef<HTMLButtonElement>(null)
  const checkboxRef = useRef<HTMLInputElement>(null)

  /**
   * Handle escape key to close modal
   * Close modal when user presses Escape
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onDecline()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
      
      // Focus the checkbox when modal opens for accessibility
      setTimeout(() => {
        checkboxRef.current?.focus()
      }, 100)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onDecline])

  /**
   * Handle focus trap within modal
   * Ensures keyboard navigation stays within the modal
   */
  useEffect(() => {
    if (!isOpen) return

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault()
        lastElement.focus()
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => document.removeEventListener('keydown', handleTabKey)
  }, [isOpen])

  /**
   * Handle checkbox change
   * Updates the checked state for enabling/disabling Accept button
   */
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked)
  }

  /**
   * Handle accept button click
   * Only proceeds if terms are accepted
   */
  const handleAccept = () => {
    if (isChecked) {
      onAccept()
    }
  }

  /**
   * Handle backdrop click
   * Closes modal when clicking outside the content area
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onDecline()
    }
  }

  // Don't render if modal is not open
  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
      aria-describedby="terms-modal-description"
    >
      {/* Backdrop overlay */}
      <div 
        className="absolute inset-0 bg-dark-bg/90 backdrop-blur-sm"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      
      {/* Modal content */}
      <div
        ref={modalRef}
        className={cn(
          "relative w-full max-w-2xl max-h-[90vh] overflow-y-auto",
          "glass-card border border-dark-border",
          "animate-scale-in"
        )}
      >
        {/* Header */}
        <div className="sticky top-0 bg-dark-card/95 backdrop-blur-sm border-b border-dark-border p-6 z-10">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-cyber-cyan/20 rounded-cyber flex items-center justify-center">
                <ScrollText className="w-6 h-6 text-cyber-cyan" aria-hidden="true" />
              </div>
              <div>
                <h2 
                  id="terms-modal-title" 
                  className="text-2xl font-bold text-gradient-cyber"
                >
                  Terms of Service
                </h2>
                <p className="text-text-muted text-sm">
                  Please review before connecting your wallet
                </p>
              </div>
            </div>
            <button
              onClick={onDecline}
              className="cyber-button-secondary p-2 hover:bg-status-error/20 hover:border-status-error/50"
              aria-label="Close Terms of Service modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6" id="terms-modal-description">
          {/* Important Notice */}
          <div className="glass-card border-l-4 border-l-status-warning border-y border-r border-status-warning/30 p-4 bg-status-warning/5">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-status-warning flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <h3 className="font-bold text-status-warning mb-1">Important Notice</h3>
                <p className="text-text-secondary text-sm">
                  By connecting your wallet, you agree to the terms and conditions of using CTO Pools. 
                  Please read the key points below carefully.
                </p>
              </div>
            </div>
          </div>

          {/* Key Terms Summary */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
              <FileText className="w-5 h-5 text-cyber-cyan" aria-hidden="true" />
              <span>Key Terms Summary</span>
            </h3>
            
            <div className="grid gap-3">
              {/* Risk Acknowledgment */}
              <div className="glass-card border border-dark-border/50 p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-status-error flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-semibold text-text-primary mb-1">Risk Acknowledgment</h4>
                    <p className="text-text-secondary text-sm">
                      Cryptocurrency investments are highly speculative. You may lose all deposited funds. 
                      CTO Pools is not responsible for any financial losses.
                    </p>
                  </div>
                </div>
              </div>

              {/* Non-Custodial Platform */}
              <div className="glass-card border border-dark-border/50 p-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-cyber-green flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-semibold text-text-primary mb-1">Non-Custodial Platform</h4>
                    <p className="text-text-secondary text-sm">
                      You retain full custody of your funds. All transactions occur directly on the 
                      Solana blockchain through smart contracts.
                    </p>
                  </div>
                </div>
              </div>

              {/* No Affiliation */}
              <div className="glass-card border border-dark-border/50 p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-cyber-yellow flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-semibold text-text-primary mb-1">No Third-Party Affiliation</h4>
                    <p className="text-text-secondary text-sm">
                      CTO Pools is NOT affiliated with any token or project promoted on the platform. 
                      Always do your own research (DYOR) before participating.
                    </p>
                  </div>
                </div>
              </div>

              {/* Wallet Security */}
              <div className="glass-card border border-dark-border/50 p-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-cyber-purple flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-semibold text-text-primary mb-1">Wallet Security</h4>
                    <p className="text-text-secondary text-sm">
                      You are solely responsible for maintaining the security of your wallet and 
                      private keys. We cannot recover lost funds.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Full Terms Link - Using <a> instead of <Link> since modal renders outside Router context */}
          <div className="text-center">
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-cyber-cyan hover:text-cyber-cyan/80 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <span>Read Full Terms of Service</span>
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>

          {/* Checkbox Agreement */}
          <div className="glass-card border border-cyber-cyan/30 p-4 bg-cyber-cyan/5">
            <label className="flex items-start space-x-3 cursor-pointer group">
              <input
                ref={checkboxRef}
                type="checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
                className={cn(
                  "w-5 h-5 mt-0.5 rounded border-2 border-dark-border bg-dark-surface",
                  "checked:bg-cyber-cyan checked:border-cyber-cyan",
                  "focus:ring-2 focus:ring-cyber-cyan/50 focus:ring-offset-0",
                  "transition-all duration-200 cursor-pointer"
                )}
                aria-describedby="terms-agreement-label"
              />
              <span id="terms-agreement-label" className="text-text-primary select-none group-hover:text-cyber-cyan transition-colors">
                I have read and agree to the{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyber-cyan underline hover:text-cyber-cyan/80"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>
                {' '}and acknowledge the risks associated with cryptocurrency transactions.
              </span>
            </label>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="sticky bottom-0 bg-dark-card/95 backdrop-blur-sm border-t border-dark-border p-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <button
              onClick={onDecline}
              className="cyber-button-secondary px-6 py-3"
              aria-label="Cancel and close modal"
            >
              Cancel
            </button>
            <button
              ref={acceptButtonRef}
              onClick={handleAccept}
              disabled={!isChecked}
              className={cn(
                "cyber-button-primary px-6 py-3 flex items-center justify-center space-x-2",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              )}
              aria-label={isChecked ? "Accept terms and connect wallet" : "Please check the checkbox to accept terms"}
            >
              <CheckCircle className="w-5 h-5" aria-hidden="true" />
              <span>Accept & Continue</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsOfServiceModal
